import { Injectable } from '@angular/core';

export interface Employer {
  id: string;
  company_name: string;
  business_location: string;
  employer_code: string;
  contact_email: string;
  contact_phone: string;
  registration_certificate_url: string;
  kra_pin_url: string;
  tax_compliance_url: string;
  tax_compliance_expires_at: string;
  cr12_url: string;
  cr12_expires_at: string;
  signed_mou_url: string;
  mou_status: string;
  status: string;
  created_at: string;
}

export interface UploadTarget {
  method: string;
  upload_url: string;
  file_url: string;
  object_key: string;
  expires_at: string;
  headers: Record<string, string>;
  next_action: string;
}

export interface DownloadTarget {
  method: string;
  download_url: string;
  file_url: string;
  object_key: string;
  expires_at: string;
}

export interface CountAmount {
  count: number;
  amount_cents: number;
}

export interface DashboardSummary {
  employers_total: number;
  active_employers: number;
  active_loans: CountAmount;
  pending_loans: CountAmount;
  active_users: number;
  users_with_active_investment: number;
  active_investment_cents: number;
  capital_hub_liability_cents: number;
  admin_fund_pool_balance_cents: number;
  income_after_repayment_cents: number;
  overdue_payments: CountAmount;
}

export interface LoanChargeConfig {
  id?: string;
  description: string;
  amount_cents: number;
  status: string;
}

export interface LoanConfig {
  id: string;
  max_salary_percent_basis_points: number;
  interest_rate_basis_points: number;
  status: string;
  charges: LoanChargeConfig[];
}

export interface AdminFundPool {
  id: string;
  name: string;
  balance_cents: number;
  status: string;
}

export interface AdminFundTransaction {
  id: string;
  created_at: string;
  pool_id: string;
  type: string;
  direction: string;
  amount_cents: number;
  balance_after_cents: number;
  narration: string;
  related_loan_id?: string;
}

export interface AdminFundPoolSummary {
  pool: AdminFundPool;
  transactions: AdminFundTransaction[];
}

export interface AdminUser {
  id: string;
  email: string;
  full_name: string;
  role: string;
  status: string;
  force_password_change: boolean;
}

export interface CreateAdminUserPayload {
  email: string;
  full_name: string;
  role: string;
  status: string;
}

export interface PagedResult<T> {
  items: T[];
  page: number;
  page_size: number;
  total: number;
}

export interface PortalLoginResult {
  status: string;
  token: string;
  user: {
    email: string;
    full_name: string;
    role: string;
    status: string;
    force_password_change: boolean;
  };
}

interface ApiEnvelope {
  nonce: string;
  ciphertext: string;
}

@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly rootURL = 'https://capital-hub-api.eloise.co.ke/api/v1';
  private readonly baseURL = 'https://capital-hub-api.eloise.co.ke/api/v1/admin';
  private readonly payloadKey = 'capital-hub-local-development-key';

  login(email: string, password: string): Promise<PortalLoginResult> {
    return this.postEncrypted<PortalLoginResult>('/auth/login', { email, password });
  }

  changePassword(email: string, currentPassword: string, newPassword: string): Promise<{ status: string }> {
    return this.postEncrypted<{ status: string }>('/auth/change-password', {
      email,
      current_password: currentPassword,
      new_password: newPassword
    });
  }

  requestPasswordReset(email: string): Promise<{ status: string }> {
    return this.postEncrypted<{ status: string }>('/auth/password-reset/request', { email });
  }

  dashboardSummary(): Promise<DashboardSummary> {
    return this.postEncrypted<DashboardSummary>('/dashboard/summary', {});
  }

  loanConfig(): Promise<LoanConfig> {
    return this.postEncrypted<LoanConfig>('/loans/config', {});
  }

  saveLoanConfig(payload: LoanConfig): Promise<LoanConfig> {
    return this.postEncrypted<LoanConfig>('/loans/config/save', payload);
  }

  adminFundPool(): Promise<AdminFundPoolSummary> {
    return this.postEncrypted<AdminFundPoolSummary>('/fund-pool', {});
  }

  adminFundPoolAction(payload: { direction: string; amount_cents: number; narration: string }): Promise<AdminFundPoolSummary> {
    return this.postEncrypted<AdminFundPoolSummary>('/fund-pool/action', payload);
  }

  employers(page = 1, pageSize = 10): Promise<PagedResult<Employer>> {
    return this.postEncrypted<PagedResult<Employer>>('/employers/list', { page, page_size: pageSize });
  }

  createEmployer(payload: Partial<Employer>): Promise<Employer> {
    return this.postEncrypted<Employer>('/employers', payload);
  }

  employerDetail(employerID: string): Promise<Employer> {
    return this.postEncrypted<Employer>('/employers/detail', { employer_id: employerID });
  }

  updateEmployer(employerID: string, payload: Partial<Employer>): Promise<Employer> {
    return this.postEncrypted<Employer>('/employers/update', { employer_id: employerID, ...payload });
  }

  createEmployerUser(payload: { employer_id: string; full_name: string; email: string; role: string; status: string }): Promise<AdminUser> {
    return this.postEncrypted<AdminUser>('/employers/users', payload);
  }

  employerUsers(employerID: string, page = 1, pageSize = 10): Promise<PagedResult<AdminUser>> {
    return this.postEncrypted<PagedResult<AdminUser>>('/employers/users/list', {
      employer_id: employerID,
      page,
      page_size: pageSize
    });
  }

  presignUpload(purpose: string, file: File): Promise<UploadTarget> {
    return this.postEncryptedURL<UploadTarget>(`${this.rootURL}/uploads/presign`, {
      purpose,
      file_name: file.name,
      content_type: file.type || 'application/octet-stream'
    });
  }

  async uploadFile(target: UploadTarget, file: File): Promise<string> {
    const response = await fetch(target.upload_url, {
      method: target.method || 'PUT',
      headers: target.headers || {},
      body: file
    });
    if (!response.ok) {
      throw new Error('File upload failed');
    }
    return target.file_url;
  }

  async uploadFileViaAPI(purpose: string, file: File): Promise<string> {
    const form = new FormData();
    form.append('purpose', purpose);
    form.append('file', file);
    const response = await fetch(`${this.rootURL}/uploads/proxy`, {
      method: 'POST',
      body: form
    });
    const responseText = await response.text();
    const plainText = await this.decryptResponse(responseText);
    if (!response.ok) {
      throw new Error(errorMessage(plainText) || 'Upload failed');
    }
    const target = JSON.parse(plainText) as UploadTarget;
    return target.file_url;
  }

  async documentViewURL(fileURL: string): Promise<string> {
    const target = await this.postEncryptedURL<DownloadTarget>(`${this.rootURL}/uploads/view`, { file_url: fileURL });
    return target.download_url;
  }

  adminUsers(page = 1, pageSize = 10): Promise<PagedResult<AdminUser>> {
    return this.postEncrypted<PagedResult<AdminUser>>('/users/list', { page, page_size: pageSize });
  }

  createAdminUser(payload: CreateAdminUserPayload): Promise<AdminUser> {
    return this.postEncrypted<AdminUser>('/users', payload);
  }

  setAdminUserStatus(userID: string, status: string): Promise<AdminUser> {
    return this.postEncrypted<AdminUser>('/users/status', { user_id: userID, status });
  }

  resetAdminUserPassword(userID: string): Promise<AdminUser> {
    return this.postEncrypted<AdminUser>('/users/reset-password', { user_id: userID });
  }

  private async postEncrypted<T>(path: string, payload: unknown): Promise<T> {
    return this.postEncryptedURL<T>(`${this.baseURL}${path}`, payload);
  }

  private async postEncryptedURL<T>(url: string, payload: unknown): Promise<T> {
    const body = await this.encryptJSON(JSON.stringify(payload));
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    const responseText = await response.text();
    const plainText = await this.decryptResponse(responseText);
    if (!response.ok) {
      throw new Error(errorMessage(plainText) || 'Request failed');
    }
    return JSON.parse(plainText) as T;
  }

  private async encryptJSON(plainText: string): Promise<ApiEnvelope> {
    const nonce = crypto.getRandomValues(new Uint8Array(12));
    const key = await this.payloadCryptoKey();
    const ciphertext = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: nonce, additionalData: utf8('api-payload'), tagLength: 128 },
      key,
      utf8(plainText)
    );
    return {
      nonce: base64Url(nonce),
      ciphertext: base64Url(new Uint8Array(ciphertext))
    };
  }

  private async decryptResponse(responseText: string): Promise<string> {
    let envelope: Partial<ApiEnvelope>;
    try {
      envelope = JSON.parse(responseText) as Partial<ApiEnvelope>;
    } catch {
      return responseText;
    }
    if (!envelope.nonce || !envelope.ciphertext) {
      return responseText;
    }
    const key = await this.payloadCryptoKey();
    const plain = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: base64UrlDecode(envelope.nonce), additionalData: utf8('api-payload'), tagLength: 128 },
      key,
      base64UrlDecode(envelope.ciphertext)
    );
    return textDecode(new Uint8Array(plain));
  }

  private async payloadCryptoKey(): Promise<CryptoKey> {
    const digest = await crypto.subtle.digest('SHA-256', utf8(this.payloadKey));
    return crypto.subtle.importKey('raw', digest, 'AES-GCM', false, ['encrypt', 'decrypt']);
  }
}

function utf8(value: string): Uint8Array {
  return new TextEncoder().encode(value);
}

function textDecode(value: Uint8Array): string {
  return new TextDecoder().decode(value);
}

function errorMessage(value: string): string {
  const text = value.trim();
  if (!text) return '';
  try {
    const parsed = JSON.parse(text) as { error?: string; message?: string };
    const parsedMessage = parsed.error?.trim() || parsed.message?.trim();
    return parsedMessage || text;
  } catch {
    return text;
  }
}

function base64Url(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function base64UrlDecode(value: string): Uint8Array {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - value.length % 4) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index++) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}
